import axios from "axios";
import { errorDonotShowForPath, getItemWithExpiry, setItemWithExpiry } from "../utils/Common";
import { ToastService } from "../common/Alert/alert";

const Baseurl = process.env.REACT_APP_API_BASE_URL;

function hasJwtExpired(obj: any, depth: number = 0, maxDepth: number = 10): boolean {
  if (depth > maxDepth) return false;
  if (typeof obj === 'string') {
    return obj.toLowerCase().includes('jwt expired');
  }
  if (typeof obj === 'number' || obj === null || obj === undefined) {
    return false;
  }
  if (Array.isArray(obj)) {
    return obj.some(item => hasJwtExpired(item, depth + 1, maxDepth));
  }
  if (typeof obj === 'object' && obj !== null) {
    return Object.values(obj).some(value => hasJwtExpired(value, depth + 1, maxDepth));
  }
  return false;
}


// Avoid multiple refresh requests
let isRefreshing = false;
let refreshPromise: Promise<any> | null = null;

// Request Interceptor
axios.interceptors.request.use(async (config) => {
  config.baseURL = Baseurl;

  let token = getItemWithExpiry("access_token") ?? "";
  let refreshToken = localStorage.getItem("refresh_token") ?? "";

  if (!token) {
    token = sessionStorage.getItem("access_token") ?? "";
  }
  if (!refreshToken) {
    refreshToken = sessionStorage.getItem("refresh_token") ?? "";
  }

  if (token) {
    config.headers["Authorization"] = `Bearer ${token}`;
  } else if (axios.defaults.headers.common["access"]) {
    config.headers["access"] = axios.defaults.headers.common["access"];
  }

  return config;
});

// Response Interceptor
axios.interceptors.response.use(
  async (response) => response,
  async (error) => {
    if (!error.response) return Promise.reject(error);

    const originalRequest = error.config;
    let st_type = "L";

    const expectedErrorRefreshToken =
      error.response.status === 401 ||
      (error.response.data && error.response.data.message === "jwt expired");

    if (expectedErrorRefreshToken) {
      let refreshToken = localStorage.getItem("refresh_token");

      if (!refreshToken) {
        refreshToken = sessionStorage.getItem("refresh_token");
        st_type = "S";
      }

      if (refreshToken) {
        if (!isRefreshing) {
          isRefreshing = true;
          refreshPromise = refreshAccessToken(st_type);
        }

        try {
          const newAccessToken = await refreshPromise;
          originalRequest.headers["Authorization"] = `Bearer ${newAccessToken}`;
          return axios(originalRequest);
        } catch (refreshError) {
          ToastService.error("Session expired. Please log in again.");
          import("../store/store").then(({ store }) => {
            import("../store/auth.store").then(({ logOut }) => {
              store.dispatch(logOut());
            });
          });
          return Promise.reject(refreshError);
        } finally {
          isRefreshing = false;
          refreshPromise = null;
        }
      } else {
        if (!hasJwtExpired(error.response)) {
          ToastService.error(error?.response?.data?.message);
        }
      }
    }

    let duplicateEmailError = false;
    if (error.response?.data?.message) {
      duplicateEmailError =
        typeof error.response?.data?.message === "string" &&
        /duplicate key/.test(error.response?.data?.message) &&
        /email:/.test(error.response?.data?.message);
    }

    if (!duplicateEmailError) {
      if (error.response?.data?.message === "invalid token" ||
        error.response?.data?.message === "Please authenticate") {
        ToastService.error("Session Timeout! Please log in again!");
        import("../store/store").then(({ store }) => {
          import("../store/auth.store").then(({ logOut }) => {
            store.dispatch(logOut());
          });
        });
      } else {
        if (error.response?.data?.status === "error") {
          const url = error?.response?.request?.responseURL;
          const pathAfterV1 = url?.split("/v1")[1]?.replace(/^\/+/, '');

          const excludedPaths = errorDonotShowForPath();

          if (excludedPaths.includes(pathAfterV1)) {
            ToastService.error(
              error.response?.data?.message ||
              (
                Array.isArray(error.response?.data?.error?.error?.details) &&
                error.response.data.error.error.details.length > 0 &&
                error.response.data.error.error.details[0]?.message
              ) || "Something went wrong"
            );
          } else if (!hasJwtExpired(error.response)) {
            ToastService.error(error.response);
          }
          else {
            ToastService.error(error.response?.data?.message || error.response);
          }
        } else {
          if (!hasJwtExpired(error.response)) {
            ToastService.error(error.response?.data?.message || "Something went wrong.");
          }
        }
      }
    }
    return Promise.reject(error);
  }
);

async function refreshAccessToken(st_type: string) {
  let refreshToken = localStorage.getItem("refresh_token") || sessionStorage.getItem("refresh_token");

  if (!refreshToken) {
    throw new Error("No refresh token available");
  }

  try {
    const refreshResponse = await axios.post("/auth/refresh-tokens", { token: refreshToken });

    if (refreshResponse?.data?.tokens) {
      const newAccessToken = refreshResponse.data.tokens.access;
      const newRefreshToken = refreshResponse.data.tokens.refresh;

      if (st_type === "S") {
        sessionStorage.setItem("access_token", newAccessToken);
        sessionStorage.setItem("refresh_token", newRefreshToken);
        localStorage.removeItem("access_token");
        localStorage.removeItem("refresh_token");
      } else {
        localStorage.setItem("refresh_token", newRefreshToken);
        setItemWithExpiry("access_token", newAccessToken, 1);
        sessionStorage.removeItem("access_token");
        sessionStorage.removeItem("refresh_token");
      }

      return newAccessToken;
    } else {
      throw new Error("Invalid refresh token response");
    }
  } catch (error) {
    throw error;
  }
}

// Exporting axios call methods
const httpsCall = {
  get: axios.get,
  post: axios.post,
  put: axios.put,
  delete: axios.delete,
  patch: axios.patch,
  patchForm: axios.patchForm,
  interceptors: axios.interceptors,
};

export default httpsCall;
